import React from 'react';
import {
  Link as RouterLink,
  NavLink as RouterNavLink,
  LinkProps as RouterLinkProps,
  NavLinkProps as RouterNavLinkProps,
} from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import type { LinkProps as StyleGuideLinkProps } from '@tmapy/style-guide';
import { Link as StyleGuideLink } from '@tmapy/style-guide';

import { linkMessages } from './linkMessages';
import { routeToUrl, RouteId, RouteUrlGetterParams } from './routes';
import { useAppContext } from './hooks';

type OmittedStyleGuideLinkProps = Omit<
  StyleGuideLinkProps,
  'children' | 'href' | 'download' | 'onClick'
>;

interface OnlyLinkProps<R extends RouteId> extends Partial<OmittedStyleGuideLinkProps> {
  to: R;
  params?: RouteUrlGetterParams<R>;
  Component?: React.ComponentType;
}

interface LinkProps<R extends RouteId = RouteId>
  extends OnlyLinkProps<R>,
    Omit<RouterLinkProps, 'to' | 'target'> {}

interface NavLinkProps<R extends RouteId = RouteId>
  extends OnlyLinkProps<R>,
    Omit<RouterNavLinkProps, 'to' | 'target'> {}

function isNavLinkProps(props: RouterLinkProps | RouterNavLinkProps): props is RouterNavLinkProps {
  return 'className' in props || 'style' in props;
}

const DefaultPreventedStyleGuideLink = (props: StyleGuideLinkProps) => {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    (props as any).navigate?.(); // internal method, see https://github.com/ReactTraining/react-router/blob/b77283cb75efc909c34e48c7cdcb7f50281f4055/packages/react-router-dom/modules/Link.js#L101
  };
  return (
    <StyleGuideLink {...props} onClick={handleClick}>
      {props.children}
    </StyleGuideLink>
  );
};

// TODO move to app/components
export const Link = <R extends RouteId>(props: LinkProps<R> | NavLinkProps<R>) => {
  const getUrl = routeToUrl[props.to] as
    | ((params: RouteUrlGetterParams<R> & { appType: string }) => string)
    | undefined;
  const { appType } = useAppContext();
  if (process.env.NODE_ENV !== 'production' && !getUrl) {
    console.error('Unknown route');
  }
  const { to, params, children, ...childProps } = props;
  const url = getUrl ? getUrl({ appType, ...(params as any) }) : `#${to}`;
  const Component =
    props.Component ?? (props.severity ? DefaultPreventedStyleGuideLink : undefined);

  // Use default link message if Link have no children
  const child = children ?? <FormattedMessage {...linkMessages[to]} values={params as any} />;

  if (isNavLinkProps(props)) {
    return (
      <RouterNavLink to={url} {...childProps}>
        {/* @ts-ignore TODO: Refactor tech dept */}
        {Component ? <Component>{child}</Component> : child}
      </RouterNavLink>
    );
  } else {
    return (
      <RouterLink component={Component} {...(childProps as any)} to={url}>
        {child}
      </RouterLink>
    );
  }
};
